// NEW_FILE_JUSTIFIED: New component for unified inbox list items — renders a single cross-source message item with source indicator, avatar, name, preview, and timestamp
import { memo } from 'react';
import { Send, Mail, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IUnifiedInboxItemProps } from '@/@types/messages';

function UnifiedInboxItemComponent({ item, onClick }: IUnifiedInboxItemProps) {
  const SourceIcon = item.source === 'telegram' ? Send : item.source === 'whatsapp' ? Phone : Mail;

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
        item.isUnread && 'bg-muted/30'
      )}
    >
      {/* Avatar */}
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
        style={{ backgroundColor: item.avatarColor }}
      >
        {item.initials}
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'truncate text-sm',
              item.isUnread ? 'font-semibold text-foreground' : 'text-foreground'
            )}
          >
            {item.name}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">{item.formattedTime}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <SourceIcon size={12} className="shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">
            {item.source === 'telegram'
              ? 'Telegram'
              : item.source === 'whatsapp'
                ? 'WhatsApp'
                : 'Gmail'}
            :
          </span>
          <span
            className={cn(
              'truncate text-xs',
              item.isUnread ? 'font-medium text-foreground' : 'text-muted-foreground'
            )}
          >
            {item.preview}
          </span>
        </div>
      </div>

      {/* Unread dot */}
      {item.isUnread && (
        <>
          <span className="sr-only">(unread)</span>
          <div className="h-2 w-2 shrink-0 rounded-full bg-sidebar-primary" aria-hidden="true" />
        </>
      )}
    </button>
  );
}

export const UnifiedInboxItem = memo(UnifiedInboxItemComponent);
